/**
 * The **diff-highlight** the allocation leaves render off a {@link LeafDecoration}'s
 * `baseline`. When the desktop edit view is opened as a *level-up* host, it passes the
 * pre-level-up pilot as the `baseline`, and each leaf lights the ranks that are **newly
 * allocated vs. that baseline** — so a player who just levelled sees, at a glance, what
 * they've spent since (and by contrast, the still-unspent headroom the bumped budget
 * opened).
 *
 * A *display* helper only — it never gates anything. The budget caps come from
 * `@lancer-vtt/domain` and the reducer stays the wall; the decoration is pure "what
 * changed" chrome layered on top.
 */
import { licenseRankOf, type PilotData } from "@lancer-vtt/domain";
import type { LeafDecoration } from "./types";

type HaseKey = "hull" | "agi" | "sys" | "eng";

/**
 * A resolved diff against a baseline pilot — the leaves ask it "is this rank newly
 * allocated since the baseline?" and light the row if so. `undefined` (no baseline)
 * means every query is `false`, so a creation host renders plainly.
 */
export type FieldDiff = {
  /** A mech-skill (HASE) value is newly gained if it's above the baseline's. */
  haseGained(skill: HaseKey, current: number): boolean;
  /** A skill-trigger rank is newly gained if it's above the baseline's. */
  skillGained(skillId: string, current: number): boolean;
  /** A talent rank is newly gained if it's above the baseline's. */
  talentGained(talentId: string, current: number): boolean;
  /** A license rank is newly gained if it's above the baseline's. */
  licenseGained(licenseId: string, current: number): boolean;
};

/** Build the diff from a decoration's `baseline`, or `undefined` if there is none. */
export function decorationFor(decoration: LeafDecoration | undefined): FieldDiff | undefined {
  const base = decoration?.baseline;
  if (!base) return undefined;
  return {
    haseGained: (skill, current) => current > base.mechSkills[skill],
    skillGained: (skillId, current) => current > (base.skills[skillId] ?? 0),
    talentGained: (talentId, current) => current > (base.talents[talentId] ?? 0),
    licenseGained: (licenseId, current) => current > licenseRankOf(base, licenseId),
  };
}

/** A `PilotData` snapshot to pass as a level-up baseline (a shallow structural copy). */
export function snapshotBaseline(pilot: PilotData): PilotData {
  return structuredClone(pilot);
}
